import { Directive, ElementRef, Host, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsDatepickerDirective, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { isDate, isDateValid, parseDate } from 'ngx-bootstrap/chronos';

@Directive({
	// Must match the third-party `bsDatepicker` inputs so that every date box in the app is
	// covered without editing 47 templates.
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: 'input[bsDatepicker]'
})
export class ValidDateInputDirective implements OnInit, OnDestroy {

	private lastValidText = '';
	private messageElement: HTMLElement = null;
	private changeCaptureListener: (event: Event) => void;
	private unlistenFocus: () => void;

	constructor(
		private el: ElementRef<HTMLInputElement>,
		private renderer: Renderer2,
		private translateService: TranslateService,
		private localeService: BsLocaleService,
		@Host() private picker: BsDatepickerDirective
	) {
	}

	ngOnInit() {
		this.lastValidText = this.el.nativeElement.value;

		// The datepicker writes whatever was typed straight into ngModel from its own 'change'
		// handler, and 'change' fires before 'blur'. Listening in the capture phase is what lets us
		// replace the bad text first, so the page never receives an invalid date and never asks the
		// server for one.
		this.changeCaptureListener = (event: Event) => this.onChangeCapture(event);
		document.addEventListener('change', this.changeCaptureListener, true);

		// Picking a date from the calendar does not raise 'change', so remember the value on focus
		// too — otherwise we would restore a stale date after a calendar pick.
		this.unlistenFocus = this.renderer.listen(this.el.nativeElement, 'focus', () => {
			const current = this.el.nativeElement.value;
			if (this.isValidText(current))
				this.lastValidText = current;
		});
	}

	ngOnDestroy() {
		document.removeEventListener('change', this.changeCaptureListener, true);
		if (this.unlistenFocus)
			this.unlistenFocus();
	}

	private onChangeCapture(event: Event) {
		if (event.target !== this.el.nativeElement)
			return;

		const typedText = this.el.nativeElement.value;

		// An empty box means "no date chosen", which is a valid state the pages already handle.
		if (!typedText.trim() || this.isValidText(typedText)) {
			this.lastValidText = typedText;
			this.toggleMessage(false);
			return;
		}

		this.el.nativeElement.value = this.lastValidText;
		this.toggleMessage(true);

		// Stop the datepicker's own 'change' handler from running at all, so ngModel keeps the date
		// it already had and no request goes out for the invalid one.
		event.stopImmediatePropagation();
	}

	private isValidText(text: string): boolean {
		const parsed = parseDate(text, this.picker._config.dateInputFormat, this.localeService.currentLocale);
		return isDate(parsed) && isDateValid(parsed);
	}

	private toggleMessage(show: boolean) {
		if (!show) {
			if (this.messageElement)
				this.renderer.setProperty(this.messageElement, 'hidden', true);
			return;
		}

		if (!this.messageElement)
			this.messageElement = this.createMessageElement();

		this.renderer.setProperty(this.messageElement, 'hidden', false);
	}

	private createMessageElement(): HTMLElement {
		const message = this.renderer.createElement('div');
		this.renderer.addClass(message, 'text-danger');
		this.renderer.addClass(message, 'invalid-date-format');
		this.renderer.appendChild(message, this.renderer.createText(this.translateService.instant('g.invalid_date_format')));
		this.renderer.setStyle(message, 'font-size', '0.875rem');

		// Most of these boxes sit in a Bootstrap input group inside a no-wrap flex row, so a message
		// added to the row would be squeezed in beside the box rather than appear under it. Floating
		// it under the group costs no layout space, so it cannot push any page out of shape.
		const group = this.el.nativeElement.closest('.input-group');
		if (group) {
			this.renderer.setStyle(group, 'position', 'relative');
			this.renderer.setStyle(message, 'position', 'absolute');
			this.renderer.setStyle(message, 'top', '100%');
			this.renderer.setStyle(message, 'left', '0');
			this.renderer.setStyle(message, 'z-index', '5');
			this.renderer.setStyle(message, 'white-space', 'nowrap');
			this.renderer.setStyle(message, 'background-color', '#fff');
			this.renderer.appendChild(group, message);

			return message;
		}

		// The few inputs with no group around them are not laid out side by side, so an ordinary
		// full-width line under the input is enough there.
		this.renderer.setStyle(message, 'width', '100%');
		this.renderer.insertBefore(this.el.nativeElement.parentNode, message, this.el.nativeElement.nextSibling);

		return message;
	}
}
